/**
 * Cohort-wide judged/unjudged surfacer findings, pooled across a version family.
 *
 * A report-matching "version" (prompt / LLM / cutoffs) is replayed over the same
 * frozen image predictions and lands as its own replay cohort. A verdict answers
 * a question about (study, finding, report), not about the prompt, so a verdict
 * given under one version counts for every version in the family. Reads
 * `/api/reader/cohort/<cohort>/surfacer/review-state` (web-demo-backend #792),
 * which marks a finding judged when any verdict the caller may reuse covers it:
 * internal staff reuse every reviewer's, an external reviewer only their own.
 *
 * Feeds the "Only unjudged" queue: the worklist and the case viewer's Next case
 * skip a study once nobody has anything left to judge on it.
 */

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";

export interface StudyReviewState {
  candidates: number;
  judged: number;
  unjudged: number;
  /** Findings nobody has judged yet, in worklist order. */
  unjudged_findings: string[];
}

export interface CohortReviewState {
  cohort_id: string;
  pooled_cohort_ids: string[];
  /** Keyed by study_uid. Studies without surfacer candidates are left out. */
  studies: Record<string, StudyReviewState>;
}

/** cohort_id → that cohort's review state. A cohort still loading is absent. */
export type ReviewStatesByCohort = Record<string, CohortReviewState>;

export const surfacerReviewStateKeys = {
  all: ["surfacer-review-state"] as const,
  cohort: (cohortId: string) => [...surfacerReviewStateKeys.all, cohortId] as const,
};

async function fetchReviewState(cohortId: string): Promise<CohortReviewState> {
  const response = await axios.get<CohortReviewState>(
    `${getApiBaseUrl()}/api/reader/cohort/${encodeURIComponent(cohortId)}/surfacer/review-state`,
    { withCredentials: true },
  );
  return response.data;
}

/**
 * Review state for each of `cohortIds` (one request per cohort). `byCohort`
 * holds only the cohorts that have loaded; `isLoading` is true until all have.
 *
 * Callers overlay the caller's own decisions (`unjudgedFindings`), which drop a
 * finding as soon as it gets a verdict. The overlay cannot add one back, so
 * `useSurfacerDecisions` invalidates this state once a study's writes drain.
 */
export function useSurfacerReviewStates(
  cohortIds: readonly string[],
  enabled: boolean,
): { byCohort: ReviewStatesByCohort; isLoading: boolean } {
  const results = useQueries({
    queries: cohortIds.map((cohortId) => ({
      queryKey: surfacerReviewStateKeys.cohort(cohortId),
      queryFn: () => fetchReviewState(cohortId),
      enabled: enabled && !!cohortId,
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
    })),
  });
  const isLoading = enabled && results.some((r) => !r.data && !r.isError);
  // Rebuilt only when a cohort's data changes, so consumers can memo on it.
  const signature = results.map((r, i) => `${cohortIds[i]}:${r.dataUpdatedAt}`).join(",");
  const byCohort = useMemo(() => {
    const out: ReviewStatesByCohort = {};
    results.forEach((result, i) => {
      if (result.data) out[cohortIds[i]] = result.data;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
  return useMemo(() => ({ byCohort, isLoading }), [byCohort, isLoading]);
}
